if (!window.Eurus?.loadedScript) {
  window.Eurus = window.Eurus || {};
  window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();
}

if (!window.Eurus.loadedScript.has('sticky-nav-top.js')) {
  window.Eurus.loadedScript.add('sticky-nav-top.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xStickyNav', (sectionId) => ({
        showNav: false,
        scrollThreshold: 300,
        activeSection: '',

        init() {
          const stickyNav = document.getElementById(`sticky-nav-${sectionId}`);
          if (stickyNav) {
            const section = stickyNav.closest('.sticky-nav-section');
            if (section && section.dataset.scrollThreshold) {
              this.scrollThreshold = parseInt(section.dataset.scrollThreshold);
            }
          }

          this.handleScroll = this.handleScroll.bind(this);
          window.addEventListener('scroll', this.handleScroll);

          this.initSectionObserver();

          this.handleScroll();

          // Ensure cleanup on Alpine component destruction
          this.$watch('$el', (value) => {
            if (!value) {
              this.destroy();
            }
          });
        },

        destroy() {
          window.removeEventListener('scroll', this.handleScroll);
          if (this.observer) {
            this.observer.disconnect();
          }
        },

        handleScroll() {
          const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
          this.showNav = scrollPosition > this.scrollThreshold;
        },

        scrollToSection(sectionId) {
          let targetElement = null;
          let strategyUsed = '';

          const sectionClassMap = {
            'product-details': '.product-page.main-product',
            'additional-information': '.product-description-accordion-container',
            'additional-info': '.product-description-accordion-container'
          };

          if (sectionClassMap[sectionId.toLowerCase()]) {
            targetElement = document.querySelector(sectionClassMap[sectionId.toLowerCase()]);
            if (targetElement) strategyUsed = 'Class mapping';
          }

          if (!targetElement) {
            targetElement = document.getElementById(sectionId);
            if (targetElement) strategyUsed = 'Exact ID match';
          }

          if (!targetElement) {
            targetElement = document.querySelector(`[id*="${sectionId}"]`);
            if (targetElement) strategyUsed = 'Partial ID match';
          }

          if (!targetElement) {
            targetElement = document.querySelector(`[data-section-id="${sectionId}"]`);
            if (targetElement) strategyUsed = 'Data attribute';
          }

          if (!targetElement) {
            const searchTerm = sectionId.toLowerCase().replace(/-/g, ' ');
            const elementsWithClasses = document.querySelectorAll('[class*="x-container"], [class*="section"], [class*="product"]');

            for (let element of elementsWithClasses) {
              const classList = element.className.toLowerCase();
              if (classList.includes(searchTerm)) {
                targetElement = element;
                strategyUsed = 'Class name search';
                break;
              }
            }
          }

          if (!targetElement) {
            const searchText = sectionId.toLowerCase().replace(/-/g, ' ');

            const headings = document.querySelectorAll('h1, h2, h3, h4, .heading, [class*="heading"]');
            for (let heading of headings) {
              const headingText = heading.textContent.toLowerCase().trim();
              if (headingText.includes(searchText) || searchText.includes(headingText)) {
                targetElement = heading.closest('section, [class*="section"], [id*="section"], .x-container-rich-text, [class*="container"]');
                if (!targetElement) {
                  targetElement = heading.parentElement;
                }
                if (targetElement) strategyUsed = 'Heading text search';
                break;
              }
            }
          }

          if (!targetElement) {
            const searchText = sectionId.toLowerCase().replace(/-/g, ' ');
            const containers = document.querySelectorAll('section, [class*="section"], .x-container-rich-text, [class*="container"]');

            for (let container of containers) {
              const containerText = container.textContent.toLowerCase().trim();
              const previewText = containerText.substring(0, 100);
              if (previewText.includes(searchText)) {
                targetElement = container;
                strategyUsed = 'Container text search';
                break;
              }
            }
          }

          if (!targetElement) {
            targetElement = document.querySelector(`[id*="x-product-template"][id*="${sectionId}"]`);
            if (targetElement) strategyUsed = 'x-product-template pattern';
          }

          if (targetElement) {
            const headerHeight = document.querySelector('.shopify-section-header')?.offsetHeight || 0;
            const stickyNavHeight = this.$el?.offsetHeight || 0;
            const offset = headerHeight + stickyNavHeight + 20; // 20px extra padding

            const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });

            this.updateActiveItem(sectionId);

          } else {
            console.warn(`✗ Section with ID "${sectionId}" not found. Tried:
              1. Class mapping (product-details → .product-page.main-product)
              2. Exact ID match
              3. Partial ID match
              4. Data attributes
              5. Class name search
              6. Heading text search
              7. Container text search
              8. x-product-template pattern`);
          }
        },

        updateActiveItem(targetSectionId) {
          const stickyNavContainer = document.querySelector('.sticky-nav-section');
          if (!stickyNavContainer) return;

          const navItems = stickyNavContainer.querySelectorAll('.nav-item');
          navItems.forEach(item => item.classList.remove('active'));

          const reviewContainer = stickyNavContainer.querySelector('[data-review-container]');
          if (reviewContainer) {
            reviewContainer.classList.remove('active');
          }

          const activeItem = stickyNavContainer.querySelector(`.nav-item[href="#${targetSectionId}"]`);
          if (activeItem) {
            activeItem.classList.add('active');
          }

          if (reviewContainer) {
            const reviewTargetSection = reviewContainer.getAttribute('data-target-section');
            if (reviewTargetSection === targetSectionId) {
              reviewContainer.classList.add('active');
            }
          }

          this.activeSection = targetSectionId;
        },

        initSectionObserver() {
          // Check for IntersectionObserver support
          if (!window.IntersectionObserver) {
            console.warn('IntersectionObserver not supported in this browser');
            return;
          }

          const options = {
            root: null,
            rootMargin: '-120px 0px -60% 0px',
            threshold: 0.1
          };

          this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                // Prefer the configured id stamped on the element below: a target may be
                // resolved by partial match, so its DOM id can differ from the configured one.
                const sectionId = entry.target.dataset.sectionId || entry.target.id;

                if (sectionId) {
                  this.updateActiveItem(sectionId);
                }
              }
            });
          }, options);

          const stickyNavContainer = document.querySelector('.sticky-nav-section');
          if (!stickyNavContainer) {
            console.warn('Sticky nav container not found');
            return;
          }

          const navItems = stickyNavContainer.querySelectorAll('.nav-item');

          navItems.forEach(item => {
            const href = item.getAttribute('href');
            if (href && href.startsWith('#')) {
              const targetId = href.substring(1);
              let targetElement = null;

              targetElement = document.getElementById(targetId);

              if (!targetElement) {
                targetElement = document.querySelector(`[id*="${targetId}"]`);
              }

              if (!targetElement) {
                targetElement = document.querySelector(`[data-section-id="${targetId}"]`);
              }

              const sectionClassMap = {
                'product-details': '.product-page.main-product',
                'additional-information': '.product-description-accordion-container',
                'additional-info': '.product-description-accordion-container'
              };

              if (!targetElement && sectionClassMap[targetId.toLowerCase()]) {
                targetElement = document.querySelector(sectionClassMap[targetId.toLowerCase()]);
              }

              if (targetElement) {
                targetElement.setAttribute('data-section-id', targetId);
                this.observer.observe(targetElement);
              }
            }
          });

          const reviewContainer = stickyNavContainer.querySelector('[data-review-container]');
          if (reviewContainer) {
            const reviewTargetId = reviewContainer.getAttribute('data-target-section');
            if (reviewTargetId) {
              let targetElement = document.getElementById(reviewTargetId);

              if (!targetElement) {
                // Use CSS.escape to prevent injection vulnerabilities
                const escapedId = CSS.escape(reviewTargetId);
                targetElement = document.querySelector(`[id*="${escapedId}"]`);
              }

              if (!targetElement) {
                // Use CSS.escape to prevent injection vulnerabilities
                const escapedId = CSS.escape(reviewTargetId);
                targetElement = document.querySelector(`[data-section-id="${escapedId}"]`);
              }

              if (targetElement) {
                targetElement.setAttribute('data-section-id', reviewTargetId);
                this.observer.observe(targetElement);
              }
            }
          }
        }
      }));
    });
  });
}
